import React, { useState, useRef, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

import { Row, Col, Card, Tabs, Descriptions, Badge, Button, Space, Typography, Tag, Divider, Form, Input, Select, Checkbox, Table, DatePicker, message, Modal, Spin, Dropdown, Menu, InputNumber, Popover, Tooltip } from 'antd';
import debounce from 'lodash/debounce';
import {
    UserOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    HistoryOutlined,
    FileTextOutlined,
    SaveOutlined,
    ClockCircleOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    EditOutlined,
    CheckOutlined,
    PlusOutlined,
    CoffeeOutlined,
    LoadingOutlined,
    LinkOutlined,
    ArrowRightOutlined,
    DownOutlined,
    UpOutlined
} from '@ant-design/icons';
import AgentStatusBar from './CallCenter/components/AgentStatusBar';
import ActiveCallCard from './CallCenter/components/ActiveCallCard';
import ActiveCampaignsWidget from './CallCenter/components/ActiveCampaignsWidget';
import ManualOutboundWidget from './CallCenter/components/ManualOutboundWidget';
import ParticipantPanel from './CallCenter/components/ParticipantPanel';
import { formatTimeStatus, getStatusStyles } from './CallCenter/utils';
import {
    pbxService,
    authService,
    participantService,

    tagService
} from '../services/api';
import { useSoftphone } from '../context/SoftphoneContext';
import { useAuth } from '../context/AuthContext';
import ReportCDR from './reports/ReportCDR';
import { CallCenterProvider, useCallCenter } from './CallCenter/CallCenterContext';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// --- Componentes Auxiliares ---
// DebounceSelect movido a src/pages/CallCenter/components/DebounceSelect.js

// --- Tabla de Monitoreo de Cola ---
// QueueMonitoringTable movido a src/pages/CallCenter/components/QueueMonitoringTable.js


const CallCenterContent = () => {
    // [EVENT-DRIVEN] Extract real state from Softphone
    const {
        status: softphoneStatus,
        inCall: softphoneInCall,
        isRinging: softphoneIsRinging,
        makeCall
    } = useSoftphone();
    const { isLoggingOut, permissions } = useAuth(); // Get logout intent and permissions

    // --- ESTADO DEL CONTEXTO ---
    const {
        activeTab, setActiveTab,
        isSelectorModalVisible, setIsSelectorModalVisible,
        isRescheduleModalVisible, setIsRescheduleModalVisible,
        isAddCardModalVisible, setIsAddCardModalVisible,
        status, setStatus,
        isInCall, setIsInCall,
        isDialing, setIsDialing,
        formSaved, setFormSaved,
        callSeconds, setCallSeconds,
        stateSeconds, setStateSeconds,
        breakSeconds, setBreakSeconds,
        breakStatuses, setBreakStatuses,
        hasContact, setHasContact,
        isLoadingContact, setIsLoadingContact,
        currentContact, setCurrentContact,
        participantCandidates, setParticipantCandidates,
        isLoadingHistory, setIsLoadingHistory,
        recentCalls, setRecentCalls,
        currentForm, setCurrentForm,
        selectedPhone, setSelectedPhone,
        showAddParticipant, setShowAddParticipant,
        isCreatingParticipant, setIsCreatingParticipant,
        isAssociatingParticipant, setIsAssociatingParticipant,
        isSavingParticipant, setIsSavingParticipant,
        campaigns, setCampaigns,
        isLoadingCampaigns, setIsLoadingCampaigns,
        selectedCampaign, setSelectedCampaign,
        rescheduledCalls, setRescheduledCalls,
        isLoadingRescheduled, setIsLoadingRescheduled,
        isGettingNext, setIsGettingNext,
        rescheduleDate, setRescheduleDate,
        isRescheduling, setIsRescheduling,
        rescheduleCampaignFilter, setRescheduleCampaignFilter,
        currentCallId, setCurrentCallId,
        currentCall, setCurrentCall,
        getValidCallId,
        availableTags, setAvailableTags,
        availableGroups, setAvailableGroups,
        monitoredCalls, setMonitoredCalls,
        resetSessionState,
        setIsWrappingUp,
        handleSoftphoneEvent, // [EVENT-DRIVEN]

        // Forms
        form,
        addParticipantForm,
        associationForm,
        addCardForm,
        // Refs
        selectorDismissedRef,
        activeCallIdRef,
        isInboundRef,
        isRescheduledSession,
        callTimerRef,
        stateTimerRef,
        breakTimerRef,
        hasOutbound,
        handleNextContact, // exposed from context
        handleReleaseContact, // exposed from context
        setManualContactLoaded, // exposed from context
        syncWithServer,
        resetInteraction,
        currentCampaignContactId, setCurrentCampaignContactId,
        hasPendingForm, setHasPendingForm,
        isRescheduled, setIsRescheduled,
        rescheduleData, setRescheduleData
    } = useCallCenter();

    // --- ESTADO LOCAL ---
    const [isPendingWrapup, setIsPendingWrapup] = useState(false); // [NUEVO] Optimistic UI
    const [isRescheduledCollapsed, setIsRescheduledCollapsed] = useState(false); // UI state

    // Rutas protegidas para evitar ciclos infinitos
    const prevSoftphoneState = useRef({ inCall: false, ringing: false });

    // [EVENT-DRIVEN] Bridge SIP state directly into Fast Path
    useEffect(() => {
        // Solo disparar evento si hubo un cambio real en el estado del softphone
        if (softphoneInCall && !prevSoftphoneState.current.inCall) {
            handleSoftphoneEvent('connected');
        } else if (softphoneIsRinging && !prevSoftphoneState.current.ringing) {
            handleSoftphoneEvent('ringing');
        }

        // Registrar el estado actual para la próxima comparación
        prevSoftphoneState.current = { inCall: softphoneInCall, ringing: softphoneIsRinging };
    }, [softphoneInCall, softphoneIsRinging, handleSoftphoneEvent]);

    // [OPTIMISTIC UI] Visual Status Calculation
    // Jerarquía visual: Eventos locales Inmediatos > Estado Pendiente > Estado del Servidor
    let visualStatus = status; // Fallback al servidor
    if (status === 'wrapup') visualStatus = 'wrapup'; // [FIX] Prioridad absoluta al wrapup del servidor
    else if (isPendingWrapup) visualStatus = 'wrapup';
    else if (isInCall) visualStatus = 'incall';
    else if (isDialing) visualStatus = 'ringing';

    // Clear Pending State when real status catches up
    useEffect(() => {
        if (status === 'wrapup' || status === 'available' || status === 'offline') {
            if (isPendingWrapup) {
                console.log("[OptimisticUI] Server caught up with local state. Clearing pending flag.");
                setIsPendingWrapup(false);
            }
        }
    }, [status, isPendingWrapup]);

    // --- RESCHEDULE FILTER STATE ---

    const handleOpenReschedule = () => {
        setIsRescheduleModalVisible(true);
        setRescheduleDate(null);
    };

    const handleConfirmReschedule = async () => {
        if (!rescheduleDate) {
            message.warning("Seleccione fecha y hora para reagendar");
            return;
        }

        let contactId = null;
        console.log('[DEBUG] ConfirmReschedule - currentContact:', currentContact);
        if (currentContact) {
            contactId = currentContact.campaign_contact_id || (currentContact.is_campaign_contact ? currentContact.id : null);
        }

        if (!contactId) {
            console.error('[DEBUG] Missing Contact ID for Reschedule', currentContact);
            message.error("No hay contacto de campaña activo para reagendar");
            return;
        }

        setIsRescheduling(true);
        try {
            const res = await pbxService.reschedule(contactId, rescheduleDate.format('YYYY-MM-DD HH:mm:ss'));
            if (res.status === 'success') {
                message.success("Contacto reagendado correctamente");
                setIsRescheduleModalVisible(false);

                isRescheduledSession.current = true; // [NUEVO] Marcar sesión como reagendada
                setIsRescheduled(true); // [NUEVO] Estado persistente
                setRescheduleData(rescheduleDate);

                // Limpiar vista SOLO si no está en llamada ni en wrapup (modo vista previa)
                if (!isInCall && !isDialing && status !== 'wrapup' && !isPendingWrapup) {
                    setHasContact(false);
                    setCurrentContact(null);
                    setParticipantCandidates([]);
                    setShowAddParticipant(false);
                    form.resetFields();
                }

                // Refresh list
                fetchRescheduled();
            } else {
                message.error(res.message || "Error al reagendar");
            }
        } catch (e) {
            console.error(e);
            message.error("Error de conexión al reagendar");
        } finally {
            setIsRescheduling(false);
        }
    };

    // Fetch Rescheduled Calls
    const fetchRescheduled = React.useCallback(async () => {
        setIsLoadingRescheduled(true);
        try {
            // Fetch ALL rescheduled calls regardless of selected campaign (User Request)
            const res = await pbxService.getRescheduledParticipants(null);
            if (res.status === 'success') {
                setRescheduledCalls(res.data || []);
            }
        } catch (e) {
            console.error("Error fetching rescheduled", e);
        } finally {
            setIsLoadingRescheduled(false);
        }
    }, []);

    // Initial Fetch & Interval
    useEffect(() => {
        fetchRescheduled();
        const interval = setInterval(fetchRescheduled, 60000); // 1 min refresh
        return () => clearInterval(interval);
    }, [fetchRescheduled]);

    // Handle "Next Contact" (Pull)


    // Manejar "Cargar Reagendado" (Selección manual)
    const handleLoadRescheduled = async (rc) => {
        if (status !== 'available' && status !== 'wrapup') {
            message.warning('Debe estar disponsible para cargar una llamada reagendada.');
            return;
        }
        if (isInCall) {
            message.warning('Termine la gestión actual antes de cargar otro contacto.');
            return;
        }

        const hideLoading = message.loading("Cargando datos del contacto...", 0);

        try {
            // [NUEVO] Reset Completo de Sesión (Prevenir estado sucio)
            resetSessionState();

            // Reiniciar bandera de sesión - estamos iniciando una NUEVA interacción con este contacto reagendado
            isRescheduledSession.current = false;
            setIsRescheduled(true); // [NUEVO] Marcar como contacto reagendado para notificaciones condicionales

            // 1. Preparar contexto base (Info Campaña)
            let campaignMetadata = {
                campaign_id: rc.campaign_id,
                reschedule_id: rc.id,
                scheduled_at: rc.reschedule_at
            };

            try {
                const parsedMeta = typeof rc.metadata === 'string' ? JSON.parse(rc.metadata || '{}') : rc.metadata || {};
                campaignMetadata = { ...campaignMetadata, ...parsedMeta };
            } catch (metaError) {
                console.warn("Error parsing rescheduled metadata:", metaError);
            }

            // 2. Seleccionar campaña si es posible
            if (rc.campaign_id && (!selectedCampaign || selectedCampaign.id !== rc.campaign_id)) {
                const camp = campaigns.find(c => c.id === rc.campaign_id);
                if (camp) setSelectedCampaign(camp);
            }

            let finalContact = {
                id: rc.id, // campaign_contacts.id
                campaign_contact_id: rc.id,
                name: rc.participant_name || 'Desconocido',
                phone: rc.phone,
                phones: [rc.phone],
                metadata: campaignMetadata,
                is_campaign_contact: true,
                is_identified: false
            };

            // 3. Intentar identificar participante por teléfono
            try {
                const searchRes = await participantService.search(rc.phone);

                if (searchRes.status === 'success' && searchRes.data && searchRes.data.length > 0) {
                    // FILTER EXACT MATCHES only to avoid broad search confusion
                    const exactMatches = searchRes.data.filter(p => {
                        const hasPhonesArray = Array.isArray(p.phones) && p.phones.includes(rc.phone);
                        const hasPhoneString = typeof p.phone_numbers === 'string' && p.phone_numbers.includes(rc.phone);
                        return hasPhonesArray || hasPhoneString || p.phone === rc.phone;
                    });

                    if (exactMatches.length === 1) {
                        // SINGLE MATCH: Merge participant data
                        const p = exactMatches[0];
                        console.log("Rescheduled: Exact participant match found", p);
                        finalContact = {
                            ...finalContact,
                            ...p,
                            participant_id: p.id,
                            is_identified: true
                        };
                        setHasContact(true);
                        setCurrentContact(finalContact);
                        setManualContactLoaded(true); // PROTECT FROM SYNC CLEANUP
                        message.success("Contacto reagendado cargado y participante identificado.");

                    } else if (exactMatches.length > 1) {
                        // MULTIPLE MATCHES: Show selector
                        console.log("Rescheduled: Multiple candidates", exactMatches);
                        setParticipantCandidates(exactMatches);
                        setCurrentContact(finalContact);
                        setHasContact(true);
                        setManualContactLoaded(true); // PROTECT FROM SYNC CLEANUP
                        setIsSelectorModalVisible(true);
                        message.info("Número asociado a múltiples participantes. Por favor seleccione.");

                    } else {
                        // NO EXACT MATCH
                        setCurrentContact(finalContact);
                        setHasContact(true);
                        setManualContactLoaded(true); // PROTECT FROM SYNC CLEANUP
                        setShowAddParticipant(true);
                        addParticipantForm.setFieldsValue({
                            phone: rc.phone,
                            fullName: rc.participant_name || ''
                        });
                    }
                } else {
                    // NO MATCHES FOUND
                    setCurrentContact(finalContact);
                    setHasContact(true);
                    setManualContactLoaded(true); // PROTECT FROM SYNC CLEANUP
                    setShowAddParticipant(true);
                    addParticipantForm.setFieldsValue({
                        phone: rc.phone,
                        fullName: rc.participant_name || ''
                    });
                }
            } catch (searchError) {
                console.error("Error identifying participant for rescheduled call:", searchError);
                if (searchError.response) {
                    console.error("Server Response:", searchError.response.status, searchError.response.data);
                }
                // Fallback: Load as unidentified
                setCurrentContact(finalContact);
                setHasContact(true);
                setManualContactLoaded(true); // PROTECT FROM SYNC CLEANUP
                setShowAddParticipant(true);
                message.warning("Cargado sin identificación de participante (Error de búsqueda).");

                // Pre-fill form in fallback case
                addParticipantForm.setFieldsValue({
                    phone: rc.phone,
                    fullName: rc.participant_name || ''
                });
            }

            // DO NOT set isRescheduledSession.current = true here. 
            // We just LOADED it. We haven't rescheduled THIS attempt yet.

        } catch (error) {
            console.error("Error loading rescheduled contact:", error);
            message.error("Error al cargar los datos del contacto.");
        } finally {
            hideLoading();
        }
    };


    // --- LÓGICA DE RESILIENCIA: PERSISTENCIA DE FORMULARIO ---
    const DRAFT_KEY = 'stc_agent_form_draft';

    // 1. Auto-save Draft on Form Change
    const handleFormChange = debounce((changedValues, allValues) => {
        if (isInCall || status === 'wrapup' || hasContact) {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(allValues));
        }
    }, 500);

    // 2. Clear Draft on Successful Submit or Discard
    const clearDraft = () => {
        localStorage.removeItem(DRAFT_KEY);
        setFormSaved(true);
    };

    // 3. Prevent Accidental Close (beforeunload)
    React.useEffect(() => {
        // Skip listener if we are intentionally logging out
        if (isLoggingOut) return;

        const handleBeforeUnload = (e) => {
            // Warn if in call or if we have unsaved form data (draft exists or dirty)
            if (isInCall || (currentForm && !formSaved)) {
                e.preventDefault();
                e.returnValue = ''; // Trigger browser standard warning
                return "";
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isInCall, currentForm, formSaved, isLoggingOut]);

    const autoAssociatedCallIdRef = React.useRef(null);



    // --- History Handler ---


    // Derived states for campaign detection
    const hasInbound = campaigns.some(c => c.type === 'inbound');




    // --- LÓGICA DE TEMPORIZADOR: TEMPORIZADORES DUALES ---

    // 1. Mantener referencias de marcas de tiempo para conteo preciso (prevenir desfase)
    const [timestamps, setTimestamps] = useState({
        dailyAccumulatedBase: 0, // Segundos acomulados hoy (cerrados)
        currentStatusStart: null, // Timestamp (Date or string) inicio estado actual
        activeCallStart: null // Timestamp inicio llamada
    });

    // 2. Intervalo Principal (Actualiza UI cada segundo)
    React.useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();

            // A. Temporizador Primario (Acumulado Diario)
            if (timestamps.currentStatusStart && status !== 'online' && status !== 'offline') {
                const elapsedInCurrent = Math.floor((now - new Date(timestamps.currentStatusStart)) / 1000);
                // Total = (Historial Cerrado) + (Duración en Vivo Actual)
                // Asegurar no negativo
                setStateSeconds((timestamps.dailyAccumulatedBase || 0) + Math.max(0, elapsedInCurrent));

                // B. Temporizador Secundario (Duración Estado Actual)
                // Reemplaza uso de breakSeconds
                // setCurrentStatusSeconds es esencialmente 'elapsedInCurrent'
                setBreakSeconds(Math.max(0, elapsedInCurrent)); // Usando nombre de variable de estado breakSeconds existente para minimizar impacto de refactorización en render

            } else {
                // Si offline/online, solo mostrar base estática (o 0)
                if (status === 'online' || status === 'offline') {
                    setStateSeconds(timestamps.dailyAccumulatedBase || 0);
                    setBreakSeconds(0);
                }
            }

            // C. Call Timer
            if (isInCall && timestamps.activeCallStart) {
                const elapsedCall = Math.floor((now - new Date(timestamps.activeCallStart)) / 1000);
                setCallSeconds(Math.max(0, elapsedCall));
            }

        }, 1000);

        return () => clearInterval(timer);
    }, [timestamps, status, isInCall]);

    // Cleanup old incremental effects
    // Removed old effects lines 180-191, 297-310, 313-325

    React.useEffect(() => {
        const fetchStaticData = async () => {
            setIsLoadingCampaigns(true);
            try {
                const [breaksData, campaignsData] = await Promise.all([
                    pbxService.getBreaks(),
                    pbxService.getActiveCampaigns()
                ]);

                if (breaksData.status === 'success') setBreakStatuses(breaksData.data);
                if (campaignsData.status === 'success') {
                    setCampaigns(campaignsData.data);
                    // Set first OUTBOUND campaign as default
                    const firstOutbound = campaignsData.data.find(c => c.type === 'outbound');
                    if (firstOutbound) setSelectedCampaign(firstOutbound);
                }
            } catch (error) {
                console.error('Error fetching static data:', error);
            } finally {
                setIsLoadingCampaigns(false);
            }
        };

        fetchStaticData();
    }, []);















    // --- CALL END HANDLER (AUTO-STATUS) ---
    // Listen for softphone hangup to update contact status if needed
    const handleSoftphoneCallEnded = React.useCallback(async (e) => {
        console.log("Softphone Call Ended Event Received", e.detail);
        const { reason, duration } = e.detail || {};

        // [NUEVO] Lógica de Refactorización: Decoupled Contact Status Update
        if (currentCampaignContactId) {
            console.log(`[Flow] Call ended for Contact ID: ${currentCampaignContactId}. Rescheduled: ${isRescheduled}`);

            // 1. Si se marcó como "Reagendar"
            if (isRescheduled) {
                console.log("[Flow] Contact already marked as Rescheduled. Backend update assumed done via handleConfirmReschedule.");
                // Note: reschedule.php already updates pbx_campaign_contacts.
                // We just need to clear or proceed.
            }
            // 2. Si NO se reagendó
            else {
                // Caso A: Sin Formulario -> Actualizar a 'contacted' inmediatamente
                if (!hasPendingForm) {
                    console.log("[Flow] No Form for this campaign. Auto-updating contact to 'contacted'. ID:", currentCampaignContactId);
                    try {
                        await pbxService.updateCampaignContactStatus(currentCampaignContactId, 'contacted');
                        console.log("[DEBUG] Auto-update 'contacted' successful for ID:", currentCampaignContactId);
                        message.success("Contacto marcado como gestionado.");
                    } catch (err) {
                        console.error("Error auto-updating contacted status", err);
                    }
                }
                // Caso B: Con Formulario -> Esperar (Wrapup)
                else {
                    console.log("[Flow] Campaign has Form. Waiting for manual save in Wrapup.");
                    // Wrapup is handled by AGI/Backend in Asterisk when call ends.
                    // The agent is now in Wrapup in the portal (syncWithServer will catch this).

                    // [NUEVO] Force Wrapup Locally to prevent 'Available' status from backend nuking the form
                    setIsWrappingUp(true);
                }
            }
        }

        // Cleanup local states for the call
        setIsInCall(false);
        setIsDialing(false);
        setCallSeconds(0);
        // ...
    }, [currentCampaignContactId, isRescheduled, hasPendingForm]);

    React.useEffect(() => {
        const onEnd = (e) => handleSoftphoneCallEnded(e);
        window.addEventListener('softphone-call-ended', onEnd);
        return () => window.removeEventListener('softphone-call-ended', onEnd);
    }, [handleSoftphoneCallEnded]);

    // Removed old independent timer effects to avoid double counting or conflict.
    // Logic is now centralized in the main interval above using timestamps.

    // fts moved to utils

    // Load tags on mount
    React.useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await tagService.listAll();
                if (response.status === 'success') {
                    setAvailableTags(response.data);
                }
            } catch (error) {
                console.error('Error loading tags:', error);
            }
        };

        const fetchFilters = async () => {
            try {
                const response = await participantService.getFilters();
                if (response.status === 'success') {
                    setAvailableGroups(response.data.groups || []);
                }
            } catch (error) {
                console.error('Error fetching filters:', error);
            }
        };

        fetchTags();
        fetchFilters();
    }, []);

    // Mock data aligned with DB schema (participants, participant_cards, participant_tags)


    const getStatusStyles = (currentStatus) => {
        if (currentStatus.startsWith('break_')) {
            const breakId = parseInt(currentStatus.split('_')[1]);
            const breakItem = breakStatuses.find(b => b.id === breakId);
            return {
                bg: '#fff7e6',
                border: '#ffd591',
                text: '#fa8c16',
                label: breakItem ? breakItem.name.toUpperCase() : 'EN RECESO'
            };
        }

        switch (currentStatus) {
            case 'available': return { bg: '#f6ffed', border: '#b7eb8f', text: '#52c41a', label: 'DISPONIBLE' };
            case 'online': return { bg: '#f5f5f5', border: '#d9d9d9', text: '#8c8c8c', label: 'NO DISPONIBLE' };
            case 'offline': return { bg: '#f5f5f5', border: '#d9d9d9', text: '#8c8c8c', label: 'NO DISPONIBLE' };
            case 'incall': return { bg: '#fff1f0', border: '#ffa39e', text: '#f5222d', label: 'EN LLAMADA' };
            case 'ringing': return { bg: '#e6f7ff', border: '#91d5ff', text: '#1890ff', label: 'TIMBRANDO' };
            case 'wrapup': return { bg: '#fffbe6', border: '#ffe58f', text: '#d4b106', label: 'ESPERANDO FORMULARIO' };
            case 'break': return { bg: '#fff7e6', border: '#ffd591', text: '#fa8c16', label: 'EN RECESO' }; // Default break style
            default: return { bg: '#f5f5f5', border: '#d9d9d9', text: '#8c8c8c', label: 'NO DISPONIBLE' };
        }
    };

    const syncStatus = async (newStatus) => {
        const oldStatus = status;
        // Optimistic update
        setStatus(newStatus);

        try {
            if (oldStatus === 'offline' && (newStatus === 'available' || newStatus === 'online')) {
                await pbxService.startAgentSession();
                // If target was available, we need another call because startSession defaults to online
                if (newStatus === 'available') {
                    await pbxService.updateAgentStatus('available');
                }
            } else if (oldStatus !== 'offline' && newStatus === 'offline') {
                await pbxService.endAgentSession();
            } else if (oldStatus !== 'offline') {
                let backendStatus = newStatus;
                let breakId = null;

                if (newStatus.startsWith('break_')) {
                    backendStatus = 'break';
                    breakId = parseInt(newStatus.split('_')[1]);
                }

                if (['available', 'break', 'incall', 'wrapup', 'online'].includes(backendStatus)) {
                    await pbxService.updateAgentStatus(backendStatus, breakId);
                }
            }
        } catch (error) {
            console.error('Error syncing status:', error);
            message.error('Error de sincronización con el servidor');
            // Revert on error? Maybe not to avoid UI flickering, but depends on requirement
            // setStatus(oldStatus); 
        }
    };

    const statusStyle = getStatusStyles(status);

    const handleStatusChange = (newStatus) => {
        // Transition rules
        if (status === 'offline' && !['available', 'online'].includes(newStatus)) {
            message.warning('Desde Desconectado solo puede pasar a Conectado o Disponible.');
            return;
        }

        // Prevent changing to break/offline directly from busy/wrapup (must save form first usually, but check current rules)
        // Actually, existing rules: if available, can go to offline or any break.
        // If busy/wrapup, cannot change manually.

        if (status === 'available' && !['offline', 'available', 'online'].includes(newStatus) && !newStatus.startsWith('break_')) {
            // This condition seems to block unknown statuses, updating for dynamic breaks
            // If it's not offline or available or online, it MUST be a valid break
            if (!newStatus.startsWith('break_')) return;
        }

        if (['incall', 'ringing', 'wrapup', 'break'].includes(status)) {
            message.warning('No puede cambiar el estado manualmente mientras está en una gestión.');
            return;
        }

        syncStatus(newStatus);
    };

    const handleSave = async () => {
        try {
            // Validate form if needed
            const formValues = await form.validateFields();

            // Inject Campaign Context ID: REMOVED.
            // We now pass campaign_contact_id explicitly to saveForm.
            // No need to pollute formValues with internal IDs.

            // NEW: Block saving if Participant Form is Open (Creating or Associating)
            // showAddParticipant remains true for the "Unidentified Participant" menu, so we must check the specific flags
            if (showAddParticipant && (isCreatingParticipant || isAssociatingParticipant)) {
                message.warning('Guarde el participante o cancele la acción para proceder con la gestión de la campaña.');
                return;
            }

            // Use robust helper to get Call ID
            const callId = getValidCallId();

            if (!callId) {
                message.error('No hay llamada activa para guardar');
                return;
            }

            // Call API to save form
            // [NEW] Pass contact_status based on reschedule flag
            // [NEW] Pass campaign_contact_id for accurate update
            const campaignContactId = currentContact?.id || null;

            // LOGIC REFINEMENT:
            // 1. If we performed a Reschedule action DURING this session (isRescheduledSession.current === true),
            //    we must NOT overwrite it with 'contacted'. We should probably send 'rescheduled' or null
            //    to let the backend flow respect the reschedule.
            // 2. If we just LOADED a rescheduled contact but completed the call normally,
            //    we send 'contacted'.

            // Note: handleConfirmReschedule sets isRescheduledSession.current = true when a reschedule happens via UI.
            // handleLoadRescheduled sets it to false initially.

            let statusToSave = 'contacted';
            if (isRescheduledSession.current) {
                console.log("Session was rescheduled during interaction. Preserving 'rescheduled' status.");
                statusToSave = 'rescheduled'; // Or null, depending on backend requirement. 'rescheduled' is safer if backend supports it.
            }

            // Call API to save form
            // [NEW] Use the robust currentCampaignContactId from context refactor
            const campaignContactIdToSave = currentCampaignContactId || currentContact?.campaign_contact_id || currentContact?.id || null;

            console.log("[DEBUG] handleSave: callId=", callId, "statusToSave=", statusToSave, "campaignContactId=", campaignContactIdToSave);

            const response = await pbxService.saveForm(
                callId,
                formValues,
                statusToSave,
                campaignContactIdToSave
            );

            if (response.status === 'success') {
                setIsWrappingUp(false); // Release forced wrapup
                setFormSaved(true);
                clearDraft(); // LIMPIAR DRAFT AL GUARDAR
                message.success('Gestión guardada correctamente');
                fetchRescheduled(); // Refresh list immediately

                // Logic Update: Only clear fields immediately if NOT in a call.
                // If in a call, we keep the inputs visible until the call ends (handled by syncWithServer)
                const isStillInCall = isInCall || isDialing;

                if (!isStillInCall) {
                    setTimeout(() => {
                        resetSessionState(); // [FIX] Solo resetear el estado base de la sesion aqui
                        resetInteraction();
                    }, 1000);
                } else {
                    console.log("Call still active - Preserving form data and participant info on screen.");
                }
            } else {
                message.error('Error al guardar la gestión');
            }
        } catch (error) {
            console.error('Error saving form:', error);
            if (error.errorFields) {
                message.warning('Por favor complete todos los campos requeridos');
            } else {
                message.error('Error al guardar la gestión');
            }
        }
    };

    const handleEdit = () => {
        setFormSaved(false);
    };

    const handleClear = () => {
        resetInteraction();
        localStorage.removeItem(DRAFT_KEY); // Manual cleanup
    };






    React.useEffect(() => {
        const handleForceEnd = () => {
            if (isInCall || isDialing) {
                console.log("[EventBridge] Call Terminated - Fast UI Update");
                setIsInCall(false);
                setIsDialing(false);

                // [OPTIMISTIC UI + HYBRID SYNC]
                // Condición: Si hay formulario pendiente Y no se ha guardado
                if ((hasPendingForm || currentForm) && !formSaved) {
                    console.log("[OptimisticUI] Call ended with pending form. Forcing Wrapup.");
                    setIsPendingWrapup(true);
                    setIsWrappingUp(true); // Tell Context not to clear it
                } else {
                    console.log("[OptimisticUI] Call ended without pending form or already saved. Transitioning to Available.");
                    setIsPendingWrapup(false);
                    setIsWrappingUp(false);

                    // Si ya se guardó, podemos limpiar el formulario localmente para mayor velocidad
                    // O si era llamada manual, limpiar todo
                    if (formSaved || !currentForm) {
                        setHasContact(false);
                        setCurrentContact(null);
                        setShowAddParticipant(false);
                        form.resetFields();
                        setFormSaved(false);
                        setCurrentCall(null);
                        setCurrentCallId(null);
                    }
                }

                // Action Secundario: Sync Inmediato para reconciliar con la BD
                setTimeout(() => {
                    syncWithServer();
                }, 200);

                message.warning('Conexión finalizada desde el softphone');
            }
        };

        window.addEventListener('softphone-call-ended', handleForceEnd);
        return () => window.removeEventListener('softphone-call-ended', handleForceEnd);
    }, [isInCall, isDialing, formSaved, currentForm, hasPendingForm, setIsInCall, setIsDialing, setIsWrappingUp, syncWithServer, form, setHasContact, setCurrentContact, setShowAddParticipant, setFormSaved]);

    // Listener for Call Connected (Answered)
    /*
    useEffect(() => {
        const handleConnected = () => {
            console.log("Call Connected Event Received. Active Call ID:", activeCallIdRef.current);
            if (activeCallIdRef.current) {
                pbxService.updateCallStatus(activeCallIdRef.current, 'in_call')
                    .then(() => console.log("Call status updated to in_call via event"))
                    .catch(err => console.warn("Failed to update call status via event", err));
                setIsInCall(true);
            }
        };
        window.addEventListener('softphone-call-connected', handleConnected);
        return () => window.removeEventListener('softphone-call-connected', handleConnected);
    }, []);
    */



    const handleCancelContact = () => {
        setHasContact(false);
        setIsDialing(false);
        setCurrentContact(null);
        form.resetFields(); // Reset form fields when cancelling contact
    };



    // Handlers for ManualOutboundWidget





    // [FIX] Derive the actual campaign for the active call (especially for inbound calls),
    // since selectedCampaign is now strictly held for outbound workspace consistency.
    const activeCallCampaign = React.useMemo(() => {
        if (currentCall?.campaign_id && campaigns.length > 0) {
            const found = campaigns.find(c => c.id === parseInt(currentCall.campaign_id));
            if (found) return found;
        }
        return selectedCampaign; // fallback
    }, [currentCall, campaigns, selectedCampaign]);

    return (
        <div className="call-center-container" id="call-center-main" style={{ padding: '0px', minHeight: '600px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* 1. BARRA DE ESTADO DEL AGENTE (EXCLUSIVA DE CALL CENTER) */}
            <AgentStatusBar
                status={visualStatus} // USE VISUAL STATUS
                stateSeconds={stateSeconds}
                breakSeconds={breakSeconds}
                campaigns={campaigns}
                breakStatuses={breakStatuses}
                handleStatusChange={handleStatusChange}
            />

            {/* 2. AREA DE GESTIÓN (TABS) */}

            < Row className="call-center-main-row" gutter={[16, 16]} style={{ flex: 1, overflow: 'hidden' }}>
                {/* Lado Izquierdo: Gestión y Formulario */}
                < Col className="call-center-left-panel" xs={24} md={14} lg={16} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

                    {/* [NEW LOCATION] ACTIVE CALL INFO CARD */}
                    <Card className="call-center-management-card" id="management-card" style={{ flex: 1, display: 'flex', flexDirection: 'column' }} styles={{ body: { flex: 1, overflowY: 'auto', padding: '0 24px' } }}>

                        {/* [MOVED HERE] ACTIVE CALL INFO CARD */}
                        {/* [OPTIMISTIC UI] Use visualStatus to keep form/card visible during transition */}
                        {(isInCall || visualStatus === 'wrapup' || visualStatus === 'ringing' || isDialing) && (currentCall?.direction === 'inbound' || currentCall?.campaign_id) && (
                            <ActiveCallCard
                                currentCall={currentCall}
                                currentContact={currentContact}
                                status={visualStatus}
                                isDialing={isDialing}
                                isInCall={isInCall}
                                selectedCampaign={activeCallCampaign}
                                callSeconds={callSeconds}
                            />
                        )}

                        <Tabs
                            id="call-center-tabs"
                            className="call-center-tabs"
                            activeKey={activeTab}
                            onChange={(key) => {
                                setActiveTab(key);
                            }}
                            items={[
                                {
                                    key: '1',
                                    label: <Space><FileTextOutlined /> Gestión Actual</Space>,
                                    children: (
                                        <div className="call-management-tab" id="call-management-tab" style={{ padding: '20px 0' }}>
                                            {/* 1. Información de campañas activas - SIEMPRE VISIBLE */}
                                            <ActiveCampaignsWidget />

                                            {/* VISTA: ESPERA DE LLAMADA (Solo Inbound, si no estamos ocupados) */}
                                            {(!isDialing && !isInCall && visualStatus !== 'wrapup' && visualStatus !== 'ringing') && (
                                                <div className="waiting-state-section" id="waiting-state-section" style={{ padding: '10px 0' }}>
                                                    {/* Mostrar "Esperando llamada" SOLO si NO es outbound_only */}
                                                    {hasInbound && (
                                                        <div className="waiting-inbound-message" id="waiting-inbound-message" style={{
                                                            background: '#fafafa',
                                                            border: '1px dashed #d9d9d9',
                                                            padding: '40px 20px',
                                                            borderRadius: '12px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            marginBottom: 20
                                                        }}>
                                                            <ClockCircleOutlined style={{ fontSize: '48px', color: '#bfbfbf', marginBottom: '20px' }} />
                                                            <Title level={4} style={{ color: '#8c8c8c', margin: 0 }}>Esperando llamada...</Title>
                                                            <Text type="secondary" style={{ marginBottom: 20 }}>Manténgase en estado DISPONIBLE para recibir asignaciones.</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* VISTA: MANUAL OUTBOUND CARD (PERSISTENTE) */}
                                            {/* Se muestra siempre que haya campañas Outbound, independientemente del estado de la llamada */}
                                            <ManualOutboundWidget />

                                            {/* VISTA: LLAMADA ACTIVA + FORMULARIO */}
                                            {(isInCall || isDialing || visualStatus === 'wrapup' || visualStatus === 'ringing') && (
                                                <>
                                                    {/* 2. Información de la llamada actual */}
                                                    {/* HIDE CARD FOR MANUAL OUTBOUND CALLS (No Campaign) ?? No, keep active call info visible always if needed */}
                                                    {/* Active Call Info Card MOVED to top */}
                                                    {false && (currentCall?.direction === 'inbound' || currentCall?.campaign_id) && (
                                                        <ActiveCallCard
                                                            currentCall={currentCall}
                                                            currentContact={currentContact}
                                                            status={status}
                                                            isDialing={isDialing}
                                                            isInCall={isInCall}
                                                            selectedCampaign={selectedCampaign}
                                                            callSeconds={callSeconds}
                                                        />
                                                    )}

                                                    {/* 3. Formulario de atención DINÁMICO - OCULTO DURANTE RINGING Y SI NO HAY FORMULARIO */}
                                                    {status !== 'ringing' && currentForm && (
                                                        <>
                                                            <Divider orientation="left" plain>
                                                                <Text type="secondary">
                                                                    {currentForm?.name ? `FORMULARIO: ${currentForm.name?.toUpperCase?.() || currentForm.name}` : 'FORMULARIO DE GESTIÓN'}
                                                                </Text>
                                                            </Divider>

                                                            <Form
                                                                id="management-form"
                                                                className="management-form"
                                                                form={form}
                                                                layout="vertical"
                                                                disabled={formSaved && !isInCall && !isDialing}
                                                                onValuesChange={handleFormChange}
                                                            >
                                                                <Row gutter={16}>
                                                                    {currentForm ? (
                                                                        currentForm.fields?.map(field => (
                                                                            <Col span={field.field_type === 'text' && field.label.length > 30 ? 24 : 12} key={field.id}>
                                                                                <Form.Item
                                                                                    label={field.label}
                                                                                    name={field.field_key}
                                                                                    rules={[{ required: field.is_required, message: `${field.label} es requerido` }]}
                                                                                >
                                                                                    {field.field_type === 'enum' ? (
                                                                                        <Select placeholder={`Seleccione ${field.label}`}>
                                                                                            {field.options?.map((opt, idx) => (
                                                                                                <Option key={idx} value={opt}>{opt}</Option>
                                                                                            ))}
                                                                                        </Select>
                                                                                    ) : field.field_type === 'number' ? (
                                                                                        <Input type="number" placeholder={`Ingrese ${field.label}`} />
                                                                                    ) : (
                                                                                        <Input placeholder={`Ingrese ${field.label}`} />
                                                                                    )}
                                                                                </Form.Item>
                                                                            </Col>
                                                                        ))
                                                                    ) : (
                                                                        /* Default fields if no dynamic form defined */
                                                                        <>
                                                                            <Col span={12}>
                                                                                <Form.Item label="Resultado de Llamada" name="call_result" rules={[{ required: true }]}>
                                                                                    <Select placeholder="Selecciona resultado">
                                                                                        <Option value="contacted">Contacto Efectivo</Option>
                                                                                        <Option value="no_answer">No Contesta</Option>
                                                                                        <Option value="busy">Ocupado</Option>
                                                                                    </Select>
                                                                                </Form.Item>
                                                                            </Col>
                                                                            <Col span={24}>
                                                                                <Form.Item label="Observaciones" name="notes">
                                                                                    <Input.TextArea rows={3} placeholder="Escribe aquí tus notas..." />
                                                                                </Form.Item>
                                                                            </Col>
                                                                        </>
                                                                    )}
                                                                </Row>

                                                                <div style={{ marginTop: 20, textAlign: 'right' }}>
                                                                    <Space size="middle">
                                                                        {formSaved && !isInCall && !isDialing ? (
                                                                            <>
                                                                                <Button
                                                                                    id="btn-form-saved"
                                                                                    className="btn-form-saved"
                                                                                    size="large"
                                                                                    onClick={async () => {
                                                                                        // Clear form data and reset to available
                                                                                        form.resetFields();
                                                                                        setFormSaved(false);
                                                                                        setCurrentContact(null);
                                                                                        setHasContact(false);
                                                                                        try {
                                                                                            await pbxService.updateAgentStatus('available');
                                                                                            setStatus('available');
                                                                                        } catch (error) {
                                                                                            console.error('Error updating status:', error);
                                                                                        }
                                                                                    }}
                                                                                    icon={<CheckOutlined />}
                                                                                >
                                                                                    Gestión Guardada
                                                                                </Button>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                {(isInCall || isDialing) ? (
                                                                                    <Button
                                                                                        id="btn-save-management"
                                                                                        className="btn-save-management"
                                                                                        type="primary"
                                                                                        size="large"
                                                                                        icon={<SaveOutlined />}
                                                                                        onClick={handleSave}
                                                                                        style={{ borderRadius: '8px', padding: '0 40px' }}
                                                                                    >
                                                                                        {formSaved ? 'Actualizar Gestión' : 'Guardar Gestión'}
                                                                                    </Button>
                                                                                ) : (
                                                                                    <>
                                                                                        <Button id="btn-clear-form" className="btn-clear-form" size="large" onClick={handleClear} style={{ borderRadius: '8px' }}>Limpiar Datos</Button>
                                                                                        <Button
                                                                                            id="btn-save-wrapup"
                                                                                            className="btn-save-wrapup"
                                                                                            type="primary"
                                                                                            size="large"
                                                                                            icon={<SaveOutlined />}
                                                                                            onClick={handleSave}
                                                                                            style={{ borderRadius: '8px', padding: '0 40px' }}
                                                                                        >
                                                                                            Guardar Gestión
                                                                                        </Button>
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </Space>
                                                                </div>
                                                            </Form>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )
                                },
                                {
                                    key: '2',
                                    label: <Space><HistoryOutlined /> Historial Reciente</Space>,
                                    children: (
                                        <div className="call-history-tab" id="call-history-tab" style={{ padding: '0', height: '100%', overflowY: 'auto' }}>
                                            <ReportCDR embedded={true} hideExport={true} hideWaitColumn={true} />
                                        </div>
                                    )
                                }
                            ]}
                        />
                    </Card>
                </Col >

                {/* Lado Derecho: Información del Participante */}
                < Col className="call-center-right-panel" id="right-panel-participant" xs={24} md={10} lg={8} style={{ height: '100%', overflowY: 'auto' }}>

                    {/* TARJETA DE LLAMADAS REAGENDADAS */}
                    {rescheduledCalls.length > 0 && (
                        <Card
                            className="rescheduled-calls-card"
                            title={
                                <Space>
                                    <ClockCircleOutlined />
                                    Llamadas Reagendadas
                                    <Badge count={rescheduledCalls.length} style={{ backgroundColor: '#1890ff' }} />
                                </Space>
                            }
                            extra={
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DownOutlined style={{ transition: 'all 0.3s ease-in-out', transform: isRescheduledCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }} />}
                                    onClick={() => setIsRescheduledCollapsed(!isRescheduledCollapsed)}
                                />
                            }
                            size="small"
                            style={{ marginBottom: 16, background: '#f0f2f5', border: 'none' }}
                            styles={{ body: { padding: '0px' } }}
                        >
                            <div style={{
                                overflow: 'hidden',
                                transition: 'all 0.3s ease-in-out',
                                maxHeight: isRescheduledCollapsed ? 0 : 500,
                                opacity: isRescheduledCollapsed ? 0 : 1,
                                padding: isRescheduledCollapsed ? '0px 12px' : '8px 12px'
                            }}>
                                <Space direction="vertical" style={{ width: '100%', marginBottom: 10 }}>
                                    <Select
                                        placeholder="Filtrar por Campaña"
                                        allowClear
                                        size="small"
                                        style={{ width: '100%' }}
                                        onChange={val => setRescheduleCampaignFilter(val)}
                                    >
                                        {campaigns.filter(c => c.type === 'outbound').map(c => (
                                            <Option key={c.id} value={c.id}>{c.name}</Option>
                                        ))}
                                    </Select>
                                </Space>

                                {/* ScrollContainer with refined design */}
                                <div style={{ maxHeight: 250, overflowY: 'auto', overflowX: 'hidden' }} className="thin-scrollbar">
                                    {rescheduledCalls.filter(rc => {
                                        const matchCampaign = !rescheduleCampaignFilter || rc.campaign_id === rescheduleCampaignFilter;
                                        return matchCampaign;
                                    }).sort((a, b) => {
                                        const timeA = dayjs(a.reschedule_at);
                                        const timeB = dayjs(b.reschedule_at);
                                        const now = dayjs();
                                        const diffA = timeA.diff(now, 'minutes');
                                        const diffB = timeB.diff(now, 'minutes');

                                        // Determine categories: 0 = Vencida (<0), 1 = Proxima (0-30), 2 = Programada (>30)
                                        const getCat = (diff) => diff < 0 ? 0 : (diff <= 30 ? 1 : 2);
                                        const catA = getCat(diffA);
                                        const catB = getCat(diffB);

                                        if (catA !== catB) return catA - catB; // Sort by category first
                                        return diffA - diffB; // Then sort by oldest within category
                                    }).map(rc => {
                                        const campaignName = campaigns.find(c => c.id === rc.campaign_id)?.name || 'Campaña Desconocida';
                                        let meta = {};
                                        try {
                                            meta = typeof rc.metadata === 'string' ? JSON.parse(rc.metadata) : (rc.metadata || {});
                                        } catch (e) { }

                                        const targetTime = dayjs(rc.reschedule_at);
                                        const diffMins = targetTime.diff(dayjs(), 'minutes');

                                        // Determine UI properties based on urgency
                                        let urgencyColor = 'blue'; // Programada
                                        if (diffMins < 0) urgencyColor = 'red'; // Vencida
                                        else if (diffMins <= 30) urgencyColor = 'orange'; // Próxima

                                        return (
                                            <div key={rc.id} style={{
                                                padding: '12px',
                                                borderLeft: `4px solid ${urgencyColor}`,
                                                marginBottom: 8,
                                                background: '#ffffff',
                                                borderRadius: 8,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                    <Text type="secondary" style={{ fontSize: 10 }}>{campaignName}</Text>
                                                    <Tooltip title={targetTime.format('DD/MM/YYYY HH:mm')}>
                                                        <Tag color={urgencyColor} style={{ margin: 0, fontSize: 10 }}>
                                                            {targetTime.fromNow()}
                                                        </Tag>
                                                    </Tooltip>
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div>
                                                        <Text strong style={{ fontSize: 13, display: 'block' }}>{rc.participant_name || meta.name || meta.nombre || 'Sin Nombre'}</Text>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>{rc.phone}</Text>
                                                    </div>
                                                </div>

                                                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        style={{ flex: 1, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                                        icon={<ArrowRightOutlined />}
                                                        disabled={isInCall}
                                                        onClick={() => handleLoadRescheduled(rc)}
                                                    >
                                                        Cargar
                                                    </Button>
                                                    {meta && Object.keys(meta).length > 0 && (
                                                        <Popover
                                                            title="Metadata del Contacto"
                                                            content={
                                                                <div style={{ maxHeight: 200, overflowY: 'auto', maxWidth: 250 }}>
                                                                    <Descriptions size="small" column={1} bordered>
                                                                        {Object.entries(meta).map(([k, v]) => (
                                                                            <Descriptions.Item key={k} label={k}>{v}</Descriptions.Item>
                                                                        ))}
                                                                    </Descriptions>
                                                                </div>
                                                            }
                                                            trigger="click"
                                                        >
                                                            <Button size="small" icon={<FileTextOutlined />} />
                                                        </Popover>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {rescheduledCalls.length > 0 && rescheduledCalls.filter(rc => {
                                        const matchCampaign = !rescheduleCampaignFilter || rc.campaign_id === rescheduleCampaignFilter;
                                        return matchCampaign;
                                    }).length === 0 && (
                                            <div style={{ textAlign: 'center', padding: 10, color: '#999', fontStyle: 'italic', fontSize: 12 }}>
                                                No hay coincidencias con los filtros
                                            </div>
                                        )}
                                </div>
                            </div>
                        </Card>
                    )}


                    <ParticipantPanel />
                    {/* Modal para Reagendar */}
                    <Modal
                        title="Reagendar Contacto"
                        open={isRescheduleModalVisible}
                        onOk={handleConfirmReschedule}
                        onCancel={() => setIsRescheduleModalVisible(false)}
                        okText="Confirmar Reagendamiento"
                        cancelText="Cancelar"
                        confirmLoading={isRescheduling}
                        centered
                    >
                        <div style={{ padding: '20px 0', textAlign: 'center' }}>
                            <p>Seleccione la fecha y hora para volver a contactar a este prospecto:</p>
                            <DatePicker
                                showTime={{ format: 'HH:mm' }}
                                format="YYYY-MM-DD HH:mm"
                                onChange={(val) => setRescheduleDate(val)}
                                style={{ width: '100%' }}
                                minuteStep={15}
                            />
                        </div>
                    </Modal>
                </Col>
            </Row>
        </div>
    );
};

const CallCenterPage = () => (
    <CallCenterProvider>
        <CallCenterContent />
    </CallCenterProvider>
);

export default CallCenterPage;
